import React, { Suspense, lazy } from 'react';
import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    Divider,
    ListSubheader,
    Breadcrumbs,
    Toolbar,
    Typography,
} from '@mui/material';
import {
    Menu as MenuIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { getPmDataMenuItems } from '../config/pmNavConfig';
import { filterByPermission } from '../utils/permissions';
import QuickActions from '../components/QuickActions';
import { AssistantProvider } from '../contexts/AssistantContext';
import OrganizationSidebarHeader from '../components/OrganizationSidebarHeader';
import SidebarNavItem from '../components/SidebarNavItem';
import LanguageSwitcher from '../components/LanguageSwitcher';
import UserProfileMenu from '../components/UserProfileMenu';
import UnfiledDocumentsIndicator from '../components/documents/UnfiledDocumentsIndicator';
import { getSidebarScrollbarStyles } from '../utils/scrollbarStyles';

const ChatAssistantPanel = lazy(() => import('../components/chat/ChatAssistantPanel'));

const DRAWER_WIDTH_EXPANDED = 240;
const DRAWER_WIDTH_COLLAPSED = 72;
const SIDEBAR_COLLAPSED_KEY = 'pmSidebarCollapsed';

const MainLayout = ({ darkMode, toggleDarkMode }) => {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [assistantOpen, setAssistantOpen] = React.useState(false);
    const [assistantIntent, setAssistantIntent] = React.useState(null);
    const [assistantContext, setAssistantContext] = React.useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved ? JSON.parse(saved) : false;
    });
    const { t } = useTranslation();
    const location = useLocation();
    const params = useParams();
    const { organization } = useAuth();
    const { has } = usePermissions();
    const orgId = params.orgId;

    const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

    React.useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(sidebarCollapsed));
    }, [sidebarCollapsed]);

    const dataMenuItems = filterByPermission(getPmDataMenuItems(orgId, t), has);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleSidebarCollapseToggle = () => {
        setSidebarCollapsed((prev) => !prev);
    };

    const handleOpenAssistant = React.useCallback((options) => {
        setAssistantIntent(options?.intent ?? null);
        setAssistantContext(options?.context ?? null);
        setAssistantOpen(true);
    }, []);

    const handleAssistantOpenChange = React.useCallback((open) => {
        setAssistantOpen(open);
        if (!open) {
            setAssistantIntent(null);
            setAssistantContext(null);
        }
    }, []);

    const generateBreadcrumbs = () => {
        const pathSegments = location.pathname.split('/').filter(segment => segment);
        const appLabel = organization?.name || '-';
        const appPath = orgId ? `/app/${orgId}/overview` : '/';
        const breadcrumbs = [
            { label: appLabel, path: appPath }
        ];

        if (pathSegments.length === 0) {
            return breadcrumbs;
        }

        if (pathSegments[0] === 'app' && pathSegments[1] && orgId) {
            if (pathSegments[2] === 'overview') {
                breadcrumbs.push({ label: 'Overview', path: `/app/${orgId}/overview` });
            } else if (pathSegments[2] === 'explore') {
                breadcrumbs.push({ label: 'Explore', path: `/app/${orgId}/explore` });
            } else if (pathSegments[2] === 'quickbooks') {
                breadcrumbs.push({ label: 'QuickBooks', path: `/app/${orgId}/quickbooks` });
                if (pathSegments[3]) {
                    const section = pathSegments[3].charAt(0).toUpperCase() + pathSegments[3].slice(1);
                    breadcrumbs.push({ label: section, path: location.pathname });
                }
            } else if (pathSegments[2] === 'settings') {
                breadcrumbs.push({
                    label: t('app.organizationSettings.title'),
                    path: `/app/${orgId}/settings`,
                });
                if (pathSegments[3] === 'quickbooks') {
                    breadcrumbs.push({
                        label: 'QuickBooks mappings',
                        path: `/app/${orgId}/settings/quickbooks`,
                    });
                }
            } else if (pathSegments[2] === 'projects') {
                breadcrumbs.push({ label: 'Projects', path: `/app/${orgId}/projects` });
                if (pathSegments[3]) {
                    breadcrumbs.push({ label: pathSegments[3], path: location.pathname });
                }
            } else if (pathSegments[2] === 'transactions') {
                breadcrumbs.push({ label: 'Transactions', path: `/app/${orgId}/transactions` });
                if (pathSegments[3]) {
                    breadcrumbs.push({ label: `Transaction ${pathSegments[3]}`, path: location.pathname });
                }
            } else if (pathSegments[2] === 'invoices') {
                breadcrumbs.push({ label: t('app.invoices.title'), path: `/app/${orgId}/invoices` });
                if (pathSegments[3] === 'new') {
                    breadcrumbs.push({ label: t('app.invoices.create'), path: location.pathname });
                } else if (pathSegments[3] === 'pay') {
                    breadcrumbs.push({ label: t('app.invoices.payInvoice'), path: location.pathname });
                } else if (pathSegments[3]) {
                    breadcrumbs.push({ label: `${t('app.invoices.invoice')} ${pathSegments[3]}`, path: location.pathname });
                }
            } else if (pathSegments[2] === 'products') {
                breadcrumbs.push({ label: t('app.products.title'), path: `/app/${orgId}/products` });
                if (pathSegments[3] === 'new') {
                    breadcrumbs.push({ label: t('app.products.create'), path: location.pathname });
                } else if (pathSegments[3] && pathSegments[4] === 'edit') {
                    breadcrumbs.push({ label: pathSegments[3], path: `/app/${orgId}/products/${pathSegments[3]}` });
                    breadcrumbs.push({ label: t('app.products.editTitle'), path: location.pathname });
                } else if (pathSegments[3]) {
                    breadcrumbs.push({ label: pathSegments[3], path: location.pathname });
                }
            } else if (pathSegments[2] === 'accounts') {
                breadcrumbs.push({ label: t('app.fundingSources.title'), path: `/app/${orgId}/accounts` });
            } else if (pathSegments[2] === 'suppliers') {
                breadcrumbs.push({ label: t('app.suppliers.title'), path: `/app/${orgId}/suppliers` });
                if (pathSegments[3]) {
                    breadcrumbs.push({
                        label: `Supplier ${pathSegments[3]}`,
                        path: location.pathname,
                    });
                }
            } else if (pathSegments[2] === 'activities') {
                breadcrumbs.push({ label: t('app.metadata.activities.title'), path: `/app/${orgId}/activities` });
            } else if (pathSegments[2] === 'project-templates') {
                breadcrumbs.push({ label: t('app.metadata.projectTemplates.title'), path: `/app/${orgId}/project-templates` });
            } else if (pathSegments[2] === 'data-fields') {
                breadcrumbs.push({ label: t('app.metadata.dataFields.title'), path: `/app/${orgId}/data-fields` });
            } else if (pathSegments[2] === 'sections') {
                breadcrumbs.push({ label: t('app.metadata.sections.title'), path: `/app/${orgId}/sections` });
            }
        }

        return breadcrumbs;
    };

    const drawerPaperSx = {
        boxSizing: 'border-box',
        width: drawerWidth,
        transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    };

    const renderDrawerContent = (collapsed) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <OrganizationSidebarHeader collapsed={collapsed} />

            <Box
                sx={(theme) => ({
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    ...getSidebarScrollbarStyles(theme),
                })}
            >
                <QuickActions
                    collapsed={collapsed}
                    onOpenAssistant={handleOpenAssistant}
                />
                <Divider />

                <List disablePadding>
                    {dataMenuItems.length > 0 && !collapsed && (
                        <ListSubheader
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: 'transparent',
                                color: 'text.primary',
                            }}
                        >
                            Data
                        </ListSubheader>
                    )}
                    {dataMenuItems.map((item) => {
                        const isActive = location.pathname === item.path
                            || (item.path !== '/' && location.pathname.startsWith(item.path));
                        return (
                            <SidebarNavItem
                                key={item.path}
                                icon={item.icon}
                                label={item.text}
                                to={item.path}
                                selected={isActive}
                                collapsed={collapsed}
                            />
                        );
                    })}
                </List>
            </Box>

            <Box
                sx={{
                    p: 1,
                    display: 'flex',
                    justifyContent: collapsed ? 'center' : 'flex-end',
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <IconButton
                    onClick={handleSidebarCollapseToggle}
                    size="small"
                    aria-label={
                        collapsed
                            ? t('app.layout.expandSidebar')
                            : t('app.layout.collapseSidebar')
                    }
                    title={
                        collapsed
                            ? t('app.layout.expandSidebar')
                            : t('app.layout.collapseSidebar')
                    }
                >
                    {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </Box>
        </Box>
    );

    const breadcrumbs = generateBreadcrumbs();

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    transition: (theme) => theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        sx={{ color: 'inherit', '& .MuiBreadcrumbs-separator': { color: 'inherit' } }}
                    >
                        {breadcrumbs.map((breadcrumb, index) => {
                            const isLast = index === breadcrumbs.length - 1;
                            return isLast ? (
                                <Typography key={breadcrumb.path} color="inherit" variant="body2">
                                    {breadcrumb.label}
                                </Typography>
                            ) : (
                                <Link
                                    key={breadcrumb.path}
                                    to={breadcrumb.path}
                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                >
                                    <Typography variant="body2" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                                        {breadcrumb.label}
                                    </Typography>
                                </Link>
                            );
                        })}
                    </Breadcrumbs>
                    <Box sx={{ flexGrow: 1 }} />
                    <UnfiledDocumentsIndicator />
                    <LanguageSwitcher />
                    <IconButton
                        color="inherit"
                        onClick={toggleDarkMode}
                        sx={{
                            ml: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                    </IconButton>
                    <UserProfileMenu />
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{
                    width: { sm: drawerWidth },
                    flexShrink: { sm: 0 },
                    transition: (theme) => theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            ...drawerPaperSx,
                            width: DRAWER_WIDTH_EXPANDED,
                        },
                    }}
                >
                    {renderDrawerContent(false)}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': drawerPaperSx,
                    }}
                    open
                >
                    {renderDrawerContent(sidebarCollapsed)}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 1,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    transition: (theme) => theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Toolbar />
                <AssistantProvider openAssistant={handleOpenAssistant}>
                    <Outlet />
                </AssistantProvider>
            </Box>
            {orgId && (
                <Suspense fallback={null}>
                    <ChatAssistantPanel
                        open={assistantOpen}
                        onOpenChange={handleAssistantOpenChange}
                        assistantIntent={assistantIntent}
                        assistantContext={assistantContext}
                    />
                </Suspense>
            )}
        </Box>
    );
};

export default MainLayout;
